import RetailPromotionNotice from "../RetailPromotionNotice";
import {
    LocalOfferOutlined,
    LoyaltyOutlined,
    PersonAddAlt1,
} from "@mui/icons-material";
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { useIsMutating } from "@tanstack/react-query";
import classNames from "classnames";
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import CustomerDialog from "../../pages/PosPage/components/CustomerDialog/CustomerDialog";
import SmallTablePlaceholder from "../SmallTablePlaceholder";
import {
    resolvePosProfileDiscountPercentages,
    toggleDiscountPercentage,
} from "../../utils/discountOptions";
import { fetchExchangeRateWithFallback } from "../../utils/exchangeRate";
import { providerSelectionAmounts, hasProviderBalance } from "../../utils/providerPaymentSelection";
import clear from "./clear.svg";
import { useStyles } from "./styles";

import { PosContext } from "../../shared/PosContext";
import { AppContext } from "../../AppContext";
import { useGetDocTypeDetails } from "../../shared/hooks/useFrappeDoctype";
import axios from "axios";

const AddPaymentWidget = ({
    terminalProviders = {},
    terminalDevices = [],
    terminalWorkstation,
    terminalError,
    selectedTip,
    selectedDiscount,
    onUpdateTip,
    onUpdateDiscount,
    onConfirm,
    handlePropertyChange,
    isCreatingInvoice,
    backCallback,
    isLoading,
    setPaymentType,
    presetDiscount,
    presetDiscountAmount,
    orderOnHouse,
    setOrderOnHouse,
    setSelectedCustomer,
    selectedCustomer,
    payingAll,
    order,
    invoice,
    fixedDiscountAmount,
    setFixedDiscountAmount,
    discountPercentage,
    setDiscountPercentage,
    staffMealEmployees,
    onStaffMealEmployeeChange,
    loyalty,
    hasActiveLoyaltyProgram = false,
    isLoyaltyLoading,
    loyaltyError,
    redeemLoyaltyPoints,
    setRedeemLoyaltyPoints,
    redeemedPoints,
    setRedeemedPoints,
    loyaltyAmount,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();


    const {
        refetchCustomers,
        refetchAddresses,
        refetchTablesData,
        posProfileCompanyMismatch,
    } =
        useContext(PosContext);

    const { discountAmountAllowed, posProfile, restaurantSettings, isRetailPos } =
        useContext(AppContext);
    const isMutating = useIsMutating();
    const hasBlockingMutation = !isRetailPos && isMutating > 0;

    const [internalMessage, setInternalMessage] = useState(null);
    const [dollarRate, setDollarRate] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [changeCustomerDialogOpen, setChangeCustomerDialogOpen] =
        useState(false);
    const [retailDiscountOpen, setRetailDiscountOpen] = useState(false);
    const retailOnDemandDiscountEnabled =
        Number(posProfile?.rm_allow_on_demand_discount) === 1;
    let promotionOffer = null;
    try { promotionOffer = JSON.parse(invoice?.rm_promotion_snapshot || "null"); } catch {}
    const canShowDiscountControls =
        !promotionOffer &&
        userCanGiveDiscount() &&
        (!isRetailPos ||
            (retailOnDemandDiscountEnabled && retailDiscountOpen));
    const discountPercentageOptions = useMemo(
        () =>
            resolvePosProfileDiscountPercentages(
                posProfile?.rm_discount_options
            ),
        [posProfile?.rm_discount_options]
    );

            // New state for partial payments
    const [cashAmount, setCashAmount] = useState(0);
    const [creditAmount, setCreditAmount] = useState(0);

    // Payment methods driven by POS Profile
    const paymentMethods = useMemo(() => {
        const methods =
            posProfile?.payments
                ?.map((p) => p?.mode_of_payment)
                ?.filter((method) => method && method !== "Provider") || [];
        // Fallback for safety if profile is missing/empty
        return methods.length > 0 ? methods : ["Cash", "Credit Card"];
    }, [posProfile?.payments]);

    const companyCurrency = useMemo(() => {
        return posProfile?.currency || "ILS";
    }, [posProfile?.currency]);

    const exchangeRateDifference = useMemo(() => {
        const parsed = Number(posProfile?.exchange_rate_difference);
        return Number.isFinite(parsed) ? parsed : 0;
    }, [posProfile?.exchange_rate_difference]);

    const defaultPaymentMethod = useMemo(() => {
        const defaultFromProfile = posProfile?.payments?.find((p) => p?.default == 1)
            ?.mode_of_payment;
        return defaultFromProfile || paymentMethods[0];
    }, [posProfile?.payments, paymentMethods]);

    // Amounts per payment method (supports mixed payments)
    const [paymentAmounts, setPaymentAmounts] = useState({});
    const [activePaymentMethod, setActivePaymentMethod] = useState(null);
    const [currentInput, setCurrentInput] = useState("0"); // Current calculator display (for active method)
    const [selectedButton, setSelectedButton] = useState("");
    const [staffMealEmployee, setStaffMealEmployee] = useState("");

    // New state for fixed discount amount
    const [discountType, setDiscountType] = useState("percentage"); // "percentage" or "fixed"

    const isStaffMeal = selectedButton === "StaffMeal";
    const isProviderPayment = selectedButton === "Provider";
    const [providerPaymentEnabled, setProviderPaymentEnabled] = useState(false);
    useEffect(() => {
        let active = true;
        axios.get("method/restaurant_management.provider_payments.availability")
            .then(({ data }) => { if (active) setProviderPaymentEnabled(data?.message?.enabled === true); })
            .catch(() => { if (active) setProviderPaymentEnabled(false); });
        return () => { active = false; };
    }, []);


    // Effect to set fixed discount amount when preset discount amount exists
    // But only for full order payments, not partial payments
    useEffect(() => {
        if (invoice) {
            setFixedDiscountAmount(invoice.discount_amount);
            setDiscountPercentage(invoice.discount_percentage);
        }
    }, [invoice, setFixedDiscountAmount, setDiscountPercentage]);

    // Track which methods were set via "Order amount" click
    const [setViaOrderAmountByMethod, setSetViaOrderAmountByMethod] = useState({});

    // Currency handling per method (account currency)
    const [methodCurrencyMap, setMethodCurrencyMap] = useState({});
    const [exchangeRatesToCompany, setExchangeRatesToCompany] = useState({});

    const inferCurrencyFromModeName = (mode) => {
        const tokens = String(mode || "")
            .trim()
            .toUpperCase()
            .split(/[^A-Z]+/)
            .filter(Boolean);
        const stopWords = new Set([
            "CASH",
            "CREDIT",
            "CARD",
            "DEBIT",
            "PAYMENT",
            "MODE",
            "OF",
            "POS",
        ]);
        for (let i = tokens.length - 1; i >= 0; i -= 1) {
            const token = tokens[i];
            if (token.length === 3 && !stopWords.has(token)) {
                return token;
            }
        }
        return null;
    };

    const hasForeignCurrencyPaymentMethod = useMemo(() => {
        const company = companyCurrency.toUpperCase();
        return paymentMethods.some((method) => {
            const methodCurrency = (
                methodCurrencyMap[method] ||
                inferCurrencyFromModeName(method) ||
                companyCurrency
            ).toUpperCase();
            return methodCurrency !== company;
        });
    }, [paymentMethods, methodCurrencyMap, companyCurrency]);

    const getCurrencySymbol = (currency) => {
        const c = (currency || "").toUpperCase();
        if (c === "ILS") return "₪";
        if (c === "USD") return "$";
        if (c === "EUR") return "€";
        if (c === "GBP") return "£";
        return c ? `${c} ` : "";
    };

    const getAdjustedExchangeRate = useCallback(
        (currency) => {
            const baseRate = Number(exchangeRatesToCompany[currency]);
            if (!Number.isFinite(baseRate)) {
                return null;
            }
            return Number((baseRate + exchangeRateDifference).toFixed(6));
        },
        [exchangeRatesToCompany, exchangeRateDifference]
    );

    // Fetch account currency for each Mode of Payment from ERPNext (best-effort).
    useEffect(() => {
        // Prime immediate best-effort currencies from mode names to avoid UI flicker.
        setMethodCurrencyMap((prev) => {
            const next = { ...prev };
            paymentMethods.forEach((method) => {
                const inferred = inferCurrencyFromModeName(method);
                if (inferred && !next[method]) {
                    next[method] = inferred;
                }
            });
            return next;
        });

        let cancelled = false;
        const company = posProfile?.company;
        if (!company || !paymentMethods.length) return;

        const fetchCurrencyForMethod = async (modeOfPayment) => {
            try {
                const { data } = await axios.get(
                    "method/restaurant_management.api.get_mode_of_payment_account_currency",
                    {
                        params: {
                            mode_of_payment: modeOfPayment,
                            company,
                        },
                    }
                );
                return data?.message || null;
            } catch (e) {
                return null;
            }
        };

        (async () => {
            const updates = {};
            for (const method of paymentMethods) {
                const cur = await fetchCurrencyForMethod(method);
                updates[method] = (cur || companyCurrency).toUpperCase();
            }
            if (!cancelled) {
                setMethodCurrencyMap((prev) => ({ ...prev, ...updates }));
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [paymentMethods, posProfile?.company, companyCurrency]);

    // Fetch exchange rates to company currency for any non-company currencies we discover.
    useEffect(() => {
        let cancelled = false;
        const needed = new Set();
        paymentMethods.forEach((m) => {
            const c = (methodCurrencyMap[m] || companyCurrency).toUpperCase();
            if (c && c !== companyCurrency.toUpperCase()) needed.add(c);
        });

        const existing = new Set(Object.keys(exchangeRatesToCompany || {}));
        const missing = Array.from(needed).filter((c) => !existing.has(c));
        if (missing.length === 0) return;

        (async () => {
            const updates = {};
            for (const fromCurrency of missing) {
                try {
                    // Special-case USD->ILS to reuse existing rate to reduce calls.
                    if (
                        fromCurrency === "USD" &&
                        companyCurrency.toUpperCase() === "ILS" &&
                        dollarRate
                    ) {
                        updates[fromCurrency] = Number(dollarRate);
                        continue;
                    }
                    const rate = await fetchExchangeRateWithFallback(
                        fromCurrency,
                        companyCurrency
                    );
                    if (rate > 0) {
                        updates[fromCurrency] = Number(rate);
                    }
                } catch (e) {
                    // ignore
                }
            }
            if (!cancelled && Object.keys(updates).length) {
                setExchangeRatesToCompany((prev) => ({ ...prev, ...updates }));
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        paymentMethods,
        methodCurrencyMap,
        companyCurrency,
        exchangeRatesToCompany,
        dollarRate,
    ]);

    const paymentAmountsInCompanyCurrency = useMemo(() => {
        const out = {};
        paymentMethods.forEach((m) => {
            const entered = Number(paymentAmounts[m]) || 0;
            const fromCurrency = (methodCurrencyMap[m] || companyCurrency).toUpperCase();
            const toCurrency = companyCurrency.toUpperCase();
            if (fromCurrency === toCurrency) {
                out[m] = entered;
                return;
            }
            const rate = getAdjustedExchangeRate(fromCurrency);
            if (!rate) {
                // Don't guess. If user entered a non-base currency amount, we must know the FX rate.
                out[m] = entered > 0 ? null : 0;
                return;
            }
            out[m] = Number((entered * rate).toFixed(2));
        });
        return out;
    }, [
        paymentAmounts,
        paymentMethods,
        methodCurrencyMap,
        companyCurrency,
        getAdjustedExchangeRate,
    ]);

    const convertCompanyToMethod = (amountCompany, method) => {
        const amt = Number(amountCompany) || 0;
        const fromCurrency = (methodCurrencyMap[method] || companyCurrency).toUpperCase();
        const toCurrency = companyCurrency.toUpperCase();
        if (fromCurrency === toCurrency) return amt;
        const rate = getAdjustedExchangeRate(fromCurrency);
        if (!rate) return null;
        return Number((amt / rate).toFixed(2));
    };


    // Ensure paymentAmounts contains keys for all methods, and active method is valid
    useEffect(() => {
        setPaymentAmounts((prev) => {
            const next = { ...prev };
            paymentMethods.forEach((m) => {
                if (next[m] == null) next[m] = 0;
            });
            // Drop removed methods
            Object.keys(next).forEach((k) => {
                if (!paymentMethods.includes(k)) {
                    delete next[k];
                }
            });
            return next;
        });
        setActivePaymentMethod((prev) => {
            if (prev && paymentMethods.includes(prev)) return prev;
            return defaultPaymentMethod || paymentMethods[0] || null;
        });
        setSetViaOrderAmountByMethod((prev) => {
            const next = { ...prev };
            paymentMethods.forEach((m) => {
                if (next[m] == null) next[m] = false;
            });
            Object.keys(next).forEach((k) => {
                if (!paymentMethods.includes(k)) {
                    delete next[k];
                }
            });
            return next;
        });
    }, [paymentMethods, defaultPaymentMethod]);

    // Local guard to prevent rapid double-click submissions
    const confirmClickLockedRef = useRef(false);

    const tables = [
        {
            name: isRetailPos
                ? t("retailPos.saleLabel")
                : t("paymentWidget.tableLabel", { number: 1 }),
            floor: "1",
            actions: [],
            canDoBack: true,
        },
    ];

    // Calculate the proper discounted amount
    const finalOrderAmount = useMemo(() => {
        if (invoice) {
            const invoiceGrandTotal = Number(invoice.grand_total) || 0;
            if (invoice.rm_promotion_snapshot) return invoiceGrandTotal;
            const invoiceDiscountAmount = Number(invoice.discount_amount) || 0;

            // `grand_total` is typically already after discount. To support changing discounts
            // in the UI, compute a best-effort "pre-discount" base and then re-apply the
            // currently selected discount.
            const baseTotal = invoiceGrandTotal + invoiceDiscountAmount;

            if (fixedDiscountAmount > 0) {
                return Math.max(0, baseTotal - fixedDiscountAmount);
            }

            if (discountPercentage > 0) {
                return Math.max(
                    0,
                    isRetailPos
                        ? Number((baseTotal - (baseTotal * discountPercentage) / 100).toFixed(2))
                        : Math.ceil(baseTotal - (baseTotal * discountPercentage) / 100)
                );
            }

            // A zero selection explicitly removes the invoice preview's discount.
            return baseTotal;
        }
        return 0;
    }, [invoice, fixedDiscountAmount, discountPercentage, isRetailPos]);

    const isLoyaltyModeBlocked =
        !!promotionOffer ||
        !payingAll ||
        selectedButton === "Deferred" ||
        isProviderPayment ||
        selectedButton === "OTH" ||
        isStaffMeal;
    const rawLoyaltyAmount = Number(loyaltyAmount) || 0;
    const appliedLoyaltyAmount =
        redeemLoyaltyPoints && !isLoyaltyModeBlocked
            ? Math.min(finalOrderAmount, rawLoyaltyAmount)
            : 0;
    const payableOrderAmount = Math.max(0, finalOrderAmount - appliedLoyaltyAmount);

    // Lock discounts once the related Table Order has been printed
    const isOrderPrinted = useMemo(() => {
        return Number(order?.invoice_printed) === 1;
    }, [order?.invoice_printed]);

    const deliveryProviderName = useMemo(() => {
        return String(order?.delivery_provider || "").trim();
    }, [order?.delivery_provider]);

    const isProviderOrder = !!deliveryProviderName;

    const { data: deliveryProvider } = useGetDocTypeDetails(
        "Delivery Providers",
        deliveryProviderName
    );

    // Provider orders block discounts unless the provider explicitly allows them
    const isProviderDiscountBlocked =
        isProviderOrder && Number(deliveryProvider?.allow_discount) !== 1;

    const allowStaffMeal = useMemo(() => {
        const settingsValue =
            restaurantSettings?.allow_staff_meal ??
            restaurantSettings?.docs?.[0]?.allow_staff_meal;
        return !isRetailPos && Number(settingsValue) === 1;
    }, [restaurantSettings, isRetailPos]);

    const totalPaidInCompanyCurrency = useMemo(() => {
        let sum = 0;
        for (const m of paymentMethods) {
            const entered = Number(paymentAmounts[m]) || 0;
            if (entered <= 0) continue;
            const converted = paymentAmountsInCompanyCurrency[m];
            if (converted === null) return null;
            sum += Number(converted) || 0;
        }
        return sum;
    }, [paymentAmountsInCompanyCurrency, paymentMethods, paymentAmounts]);

    const hasUsedForeignCurrencyPayment = useMemo(() => {
        const company = companyCurrency.toUpperCase();
        return paymentMethods.some((method) => {
            const entered = Number(paymentAmounts[method]) || 0;
            if (entered <= 0) return false;
            const methodCurrency = (
                methodCurrencyMap[method] || companyCurrency
            ).toUpperCase();
            return methodCurrency !== company;
        });
    }, [paymentMethods, paymentAmounts, methodCurrencyMap, companyCurrency]);

    const rawRemainingAmount =
        totalPaidInCompanyCurrency === null
            ? null
            : Math.max(0, payableOrderAmount - totalPaidInCompanyCurrency);

    const remainingAmount =
        rawRemainingAmount === null
            ? null
            : hasUsedForeignCurrencyPayment && rawRemainingAmount < 0.4
                ? 0
                : rawRemainingAmount;
    const changeAmount =
        totalPaidInCompanyCurrency === null
            ? null
            : Math.max(0, totalPaidInCompanyCurrency - payableOrderAmount);

    function userCanGiveDiscount() {
        return true;
    }

    // Enhanced keyboard support
    const handleKeyboardInput = useCallback(
        (event) => {
            // Avoid intercepting typing when focused inside any input fields or editable areas
            const target = event.target;
            const tagName = target?.tagName?.toLowerCase?.();
            const isTypingInField =
                tagName === "input" ||
                tagName === "textarea" ||
                target?.isContentEditable;
            if (isTypingInField) {
                return;
            }

            const key = event.key;

            // Prevent default behavior for number keys and specific keys
            if (
                /[0-9]/.test(key) ||
                ["Backspace", "Delete", "Enter", "Escape"].includes(key)
            ) {
                event.preventDefault();
            }

            if (/[0-9]/.test(key)) {
                handleUpdateAmount(parseInt(key));
            } else if (key === "Backspace" || key === "Delete") {
                handleUpdateClear();
            } else if (key === "Escape") {
                handleClearAll();
            } else if (key === "Enter") {
                // TODO: suspected to make repeated calls, not sure yet.
                // validateInput();
            }
        },
        [
            currentInput,
            activePaymentMethod,
            paymentAmounts,
            totalPaidInCompanyCurrency,
            payableOrderAmount,
            orderOnHouse,
            selectedButton,
            selectedCustomer,
        ]
    );

    useEffect(() => {
        // Add keyboard event listener
        window.addEventListener("keydown", handleKeyboardInput);

        return () => {
            window.removeEventListener("keydown", handleKeyboardInput);
        };
    }, [handleKeyboardInput]);

    // Enhanced calculator functions
    const handleUpdateAmount = (digit) => {
        let newValue = currentInput;

        // If current input is "0", replace it; otherwise append
        if (newValue === "0") {
            newValue = digit.toString();
        } else {
            newValue += digit.toString();
        }

        setCurrentInput(newValue);
        const amount = parseFloat(newValue) || 0;
        updateActivePaymentAmount(amount, true); // Skip parent update for manual calculator input

        // Clear tracking flag if amount is different from order amount
        if (amount !== payableOrderAmount && activePaymentMethod) {
            setSetViaOrderAmountByMethod((prev) => ({
                ...prev,
                [activePaymentMethod]: false,
            }));
        }
    };

    const handleReplaceAmount = (amount) => {
        // Always use current discounted amount when called from order amount click
        const actualAmount =
            amount === payableOrderAmount ? payableOrderAmount : amount;
        const displayInActive =
            actualAmount === payableOrderAmount && activePaymentMethod
                ? convertCompanyToMethod(payableOrderAmount, activePaymentMethod)
                : actualAmount;
        if (
            actualAmount === payableOrderAmount &&
            activePaymentMethod &&
            displayInActive === null
        ) {
            setErrorMessage(
                `Exchange rate not ready for ${activePaymentMethod}. Please wait.`
            );
            return;
        }
        const newValue = displayInActive.toString();
        setCurrentInput(newValue);

        // Skip parent update when setting amount via "Order amount" to prevent feedback loop
        const isOrderAmountClick = actualAmount === payableOrderAmount;
        updateActivePaymentAmount(displayInActive, isOrderAmountClick);

        // When setting via "Order amount" click, reset other methods and track this one
        if (actualAmount === payableOrderAmount && activePaymentMethod) {
            const entered = convertCompanyToMethod(payableOrderAmount, activePaymentMethod);
            if (entered === null) {
                setErrorMessage(
                    `Exchange rate not ready for ${activePaymentMethod}. Please wait.`
                );
                return;
            }
            setPaymentAmounts((prev) => {
                const next = { ...prev };
                paymentMethods.forEach((m) => {
                    next[m] = m === activePaymentMethod ? entered : 0;
                });
                return next;
            });
            setSetViaOrderAmountByMethod((prev) => {
                const next = { ...prev };
                paymentMethods.forEach((m) => {
                    next[m] = m === activePaymentMethod;
                });
                return next;
            });
        }
    };

    const handleUpdateClear = () => {
        let newValue = currentInput;

        if (newValue.length <= 1) {
            newValue = "0";
        } else {
            newValue = newValue.slice(0, -1);
        }

        setCurrentInput(newValue);
        const amount = parseFloat(newValue) || 0;
        updateActivePaymentAmount(amount, true); // Skip parent update for manual calculator input

        // Clear tracking flag if amount is different from order amount
        if (amount !== payableOrderAmount && activePaymentMethod) {
            setSetViaOrderAmountByMethod((prev) => ({
                ...prev,
                [activePaymentMethod]: false,
            }));
        }
    };

    const handleClearAll = () => {
        setCurrentInput("0");
        updateActivePaymentAmount(0, true); // Skip parent update for manual keypad input
        // Reset tracking flag for active method
        if (activePaymentMethod) {
            setSetViaOrderAmountByMethod((prev) => ({
                ...prev,
                [activePaymentMethod]: false,
            }));
        }
    };

    const updateActivePaymentAmount = (amount, skipParentUpdate = false) => {
        if (!activePaymentMethod) return;
        setSetViaOrderAmountByMethod((prev) => ({ ...prev, [activePaymentMethod]: false }));
        setPaymentAmounts((prev) => ({
            ...prev,
            [activePaymentMethod]: amount,
        }));
        // We intentionally avoid calling handlePropertyChange here; Widgets uses modeOfPayment
        // and only falls back to paymentData.amount when modeOfPayment is empty.
    };

    const handlePaymentTypeSwitch = (method) => {
        setActivePaymentMethod(method);
        const amount = Number(paymentAmounts?.[method]) || 0;
        const wasSetViaOrderAmount = !!setViaOrderAmountByMethod?.[method];
        const displayAmount = wasSetViaOrderAmount ? payableOrderAmount : amount;
        setCurrentInput(displayAmount === 0 ? "0" : displayAmount.toString());
        setErrorMessage(null);
    };

    const validateInput = () => {
        const requestedRedeemedPoints =
            redeemLoyaltyPoints && redeemedPoints > 0 ? Number(redeemedPoints) : 0;

        if (posProfileCompanyMismatch?.message) {
            setErrorMessage(posProfileCompanyMismatch.message);
            return false;
        }

        if (requestedRedeemedPoints > 0) {
            if (isLoyaltyModeBlocked) {
                setErrorMessage(t("paymentWidget.loyaltySpecialModeDisabled"));
                return false;
            }
            if (!payingAll) {
                setErrorMessage(t("paymentWidget.loyaltyFullOrderOnly"));
                return false;
            }
            if (!invoice?.customer) {
                setErrorMessage(t("paymentWidget.selectCustomer"));
                return false;
            }
            if (isLoyaltyLoading) {
                setErrorMessage(t("common.loading"));
                return false;
            }
            if (!loyalty?.hasProgram) {
                setErrorMessage(
                    loyalty?.disabledReason ||
                        t("paymentWidget.loyaltyNoProgram")
                );
                return false;
            }
            if (!loyalty?.canRedeem) {
                setErrorMessage(
                    loyalty?.disabledReason ||
                        t("paymentWidget.loyaltyUnavailable")
                );
                return false;
            }
            if (requestedRedeemedPoints > loyalty.loyaltyPoints) {
                setErrorMessage(t("paymentWidget.loyaltyPointsExceeded"));
                return false;
            }
            if (
                loyalty.minimumRedemption > 0 &&
                requestedRedeemedPoints < loyalty.minimumRedemption
            ) {
                setErrorMessage(
                    t("paymentWidget.loyaltyMinimumRedemption", {
                        points: loyalty.minimumRedemption,
                    })
                );
                return false;
            }
            if (
                loyalty.maximumRedemption > 0 &&
                requestedRedeemedPoints > loyalty.maximumRedemption
            ) {
                setErrorMessage(
                    t("paymentWidget.loyaltyMaximumRedemption", {
                        points: loyalty.maximumRedemption,
                    })
                );
                return false;
            }
            if (rawLoyaltyAmount > finalOrderAmount + 0.009) {
                setErrorMessage(t("paymentWidget.loyaltyCannotExceedAmount"));
                return false;
            }
        }

        // Check if total payment covers the order amount
        if (totalPaidInCompanyCurrency === null) {
            setErrorMessage("Exchange rate is loading. Please wait.");
            return false;
        }
        if (
            remainingAmount > 0 &&
            !orderOnHouse &&
            selectedButton !== "Deferred" &&
            !isProviderPayment &&
            !isStaffMeal
        ) {
            setErrorMessage(
                t("paymentWidget.insufficientPayment", {
                    remaining: remainingAmount.toFixed(2),
                })
            );
            return false;
        }

        // Check if at least one payment method is used (unless special cases)
        if (!orderOnHouse && selectedButton !== "Deferred" && !isProviderPayment && !isStaffMeal) {
            if (
                payableOrderAmount > 0 &&
                paymentMethods.every((m) => Number(paymentAmounts[m]) === 0)
            ) {
                setErrorMessage(t("paymentWidget.enterPaymentAmount"));

                return false;
            }
        } else if (selectedButton && selectedButton === "Deferred") {
            if (!selectedCustomer) {
                setErrorMessage(t("paymentWidget.customerNotSelected"));
                return false;
            }
        } else if (isStaffMeal) {
            if (!staffMealEmployee) {
                setErrorMessage(t("paymentWidget.staffMealEmployeeRequired"));
                return false;
            }
        }

        if (isProviderPayment && (!providerPaymentEnabled || !order?.delivery_provider)) {
            setErrorMessage(t("Select a delivery provider before paying with Provider"));
            return false;
        }
        if (isProviderPayment && (!hasProviderBalance(remainingAmount) || !payingAll)) {
            setErrorMessage(t("Provider requires a positive remaining balance on the full order. Reduce Cash or other payments first."));
            return false;
        }

        if (paymentMethods.some(method => terminalProviders[method] && Number(paymentAmounts[method]) > 0 && (methodCurrencyMap[method] || companyCurrency).toUpperCase() !== companyCurrency.toUpperCase())) {
            setErrorMessage(t("terminalPayment.currencyError", { defaultValue: "Terminal payments must use the invoice currency." }));
            return false;
        }

        // Prepare payment mode data for the payload.
        // IMPORTANT:
        // - For company-currency methods, send a NUMBER (backwards compatible).
        // - For foreign-currency methods, send the ENTERED amount (not converted),
        //   plus currency + exchange_rate (to company currency).
        const modeOfPayment = {};
        paymentMethods.forEach((m) => {
            const entered = Number(paymentAmounts[m]) || 0;
            if (entered <= 0) return;

            const fromCurrency = (methodCurrencyMap[m] || companyCurrency).toUpperCase();
            const toCurrency = companyCurrency.toUpperCase();
            const normalizedMethod =
                fromCurrency !== toCurrency &&
                m.toUpperCase().endsWith(` ${fromCurrency}`)
                    ? m.slice(0, -(fromCurrency.length + 1)).trim()
                    : m;
            const paymentKey =
                fromCurrency !== toCurrency
                    ? `Cash - ${m}`
                    : normalizedMethod;

            // Company currency -> keep the legacy numeric payload
            if (fromCurrency === toCurrency) {
                modeOfPayment[paymentKey] = entered;
                return;
            }

            // Foreign currency -> include FX metadata
            const rate = getAdjustedExchangeRate(fromCurrency);
            if (!rate) {
                setErrorMessage(
                    `Exchange rate not ready for ${m} (${fromCurrency} → ${toCurrency}). Please wait.`
                );
                return;
            }

            modeOfPayment[paymentKey] = {
                amount: entered,
                currency: fromCurrency,
                exchange_rate: Number(rate),
                invoice_mode_of_payment: normalizedMethod,
            };
        });

        // A zero marker requests server-calculated supplier remainder. It is
        // never an amount received; entered cash/card values remain unchanged.
        if (isProviderPayment) modeOfPayment.Provider = 0;

        // Update payment type for backward compatibility
        const nonZero = Object.keys(modeOfPayment);
        if (nonZero.length > 1) {
            setPaymentType("Mixed");
        } else if (nonZero.length === 1) {
            setPaymentType(nonZero[0]);
        }

        if (onConfirm) {
            // Prepare discount information to pass along with payment data
            const discountInfo = {
                discountType: discountType,
                fixedDiscountAmount:
                    discountType === "fixed" ? fixedDiscountAmount : 0,
                percentageDiscount:
                    discountType === "percentage" ? selectedDiscount : 0,
            };

            const meta =
                selectedButton === "StaffMeal"
                    ? { specialMode: "STAFF_MEAL", employee: staffMealEmployee }
                    : undefined;

            // Pass the modeOfPayment and discount info to the parent component
            onConfirm(modeOfPayment, discountInfo, meta);
        }
        return true;
    };

    useEffect(() => {
        fetchExchangeRateWithFallback("USD", "ILS")
            .then((rate) => {
                setDollarRate(Number(rate));
            })
            .catch((error) => {
                console.error("Error fetching dollar rate:", error);
            });
    }, []);

    // Auto-update payment amounts when discount changes if they were set via "Order amount" click
    useEffect(() => {
        if (!activePaymentMethod) return;
        const anySet = Object.values(setViaOrderAmountByMethod || {}).some(Boolean);
        if (!anySet) return;

        // Find which method was set via order amount (should be at most one)
        const method = Object.keys(setViaOrderAmountByMethod || {}).find(
            (m) => setViaOrderAmountByMethod[m]
        );
        if (!method) return;

        const entered = convertCompanyToMethod(payableOrderAmount, method);
        if (entered === null) {
            return;
        }
        setPaymentAmounts((prev) => ({
            ...prev,
            [method]: entered,
        }));
        if (activePaymentMethod === method) {
            setCurrentInput(entered.toString());
        }
    }, [
        payableOrderAmount,
        setViaOrderAmountByMethod,
        activePaymentMethod,
        exchangeRatesToCompany,
        methodCurrencyMap,
        companyCurrency,
    ]);

    useEffect(() => {
        if (isCreatingInvoice) {
            setInternalMessage(t("paymentWidget.creatingInvoice"));
        } else {
            setInternalMessage(null);
        }
    }, [isCreatingInvoice, t]);

    // Release click lock when no submission is in-flight
    useEffect(() => {
        if (!hasBlockingMutation && !isCreatingInvoice && !isLoading) {
            confirmClickLockedRef.current = false;
        }
    }, [hasBlockingMutation, isCreatingInvoice, isLoading]);

    const handleBackClicked = () => {
        if (backCallback) {
            backCallback();
        }
    };

    const getPaymentTypeButtonStyle = (method) => ({
        flex: 1,
        backgroundColor:
            activePaymentMethod === method ? "#e14954" : "#6E7187",
        color: "#FFFFFF",
        marginRight: "5px",
        marginLeft: "5px",
        border:
            activePaymentMethod === method ? "2px solid #000" : "1px solid #ccc",
    });

    const getButtonStyle1 = (type) => ({
        flex: 1,
        backgroundColor: selectedButton === type ? "#e14954" : "#6E7187",
        color: selectedButton === type ? "#FFFFFF" : "#FFFFFF",
        marginRight: "10px",
        border: selectedButton === type ? "1px solid #000" : "1px solid #ccc",
    });

    const handleButtonChange = (event, newButton) => {
        if (newButton === "Provider" && selectedButton !== "Provider") {
            const next = providerSelectionAmounts(paymentAmounts, setViaOrderAmountByMethod);
            setPaymentAmounts(next);
            setSetViaOrderAmountByMethod({});
            setCurrentInput(String(next[activePaymentMethod] || 0));
            setErrorMessage(null);
        }
        setSelectedButton(newButton);
    };

    useEffect(() => {
        if (
            isRetailPos &&
            (selectedButton === "StaffMeal" || selectedButton === "OTH")
        ) {
            setSelectedButton("");
            setStaffMealEmployee("");
            setOrderOnHouse(false);
            return;
        }
        if (selectedButton && selectedButton === "Deferred") {
            setOrderOnHouse(false);
        } else if (selectedButton && selectedButton === "OTH") {
            setOrderOnHouse(true);
        } else if (isStaffMeal) {
            setOrderOnHouse(false);
            // Clear any manual discounts — staff meal discount is controlled by the backend
            setFixedDiscountAmount(0);
            setDiscountPercentage(0);
            setDiscountType("percentage");
            if (onUpdateDiscount) {
                onUpdateDiscount(0);
            }
        } else {
            setOrderOnHouse(false);
        }
    }, [selectedButton, setOrderOnHouse, isRetailPos]);

    useEffect(() => {
        if (selectedButton !== "StaffMeal") {
            setStaffMealEmployee("");
            if (onStaffMealEmployeeChange) {
                onStaffMealEmployeeChange("");
            }
        }
    }, [selectedButton]);

    useEffect(() => {
        if (selectedButton !== "Deferred" && selectedCustomer) {
            setSelectedCustomer(null);
        }
    }, [selectedButton, selectedCustomer, setSelectedCustomer]);

    useEffect(() => {
        if (!redeemLoyaltyPoints) {
            if (redeemedPoints !== 0) {
                setRedeemedPoints(0);
            }
            return;
        }

        if (isLoyaltyModeBlocked || !loyalty?.canRedeem) {
            setRedeemLoyaltyPoints(false);
            setRedeemedPoints(0);
            return;
        }

        if (redeemedPoints > loyalty.loyaltyPoints) {
            setRedeemedPoints(loyalty.loyaltyPoints);
        }
    }, [
        redeemLoyaltyPoints,
        redeemedPoints,
        isLoyaltyModeBlocked,
        loyalty?.canRedeem,
        loyalty?.loyaltyPoints,
        setRedeemLoyaltyPoints,
        setRedeemedPoints,
    ]);

    const changeCustomer = (customer) => {
        setChangeCustomerDialogOpen(true);
    };

    const onConfirmCustomerChanged = (customerName, address) => {
        refetchCustomers();
        refetchAddresses();
        setSelectedCustomer(customerName);
        setErrorMessage(null);
        setChangeCustomerDialogOpen(false);
    };

    const onCustomerSelected = (customer, address) => {
        setSelectedCustomer(customer);
        setErrorMessage(null);
        setChangeCustomerDialogOpen(false);
    };

    const handleCustomerChangeClose = () => {
        setChangeCustomerDialogOpen(false);
    };

    // Handle fixed discount amount input change
    const handleFixedDiscountChange = (event) => {
        // Don't allow changes if order has already been printed
        if (isOrderPrinted) {
            return;
        }
        if (isProviderDiscountBlocked) {
            setErrorMessage(t("workMode.providerDiscountNotAllowed"));
            return;
        }
        if (isStaffMeal) {
            return;
        }
        // Don't allow changes if this is a preset discount amount
        if (presetDiscountAmount != null && presetDiscountAmount > 0) {
            return;
        }

        const value =
            event.target.value === "" ? 0 : parseFloat(event.target.value) || 0;
        setFixedDiscountAmount(value);

        if (value > 0) {
            // Switch to fixed discount mode and clear percentage discount
            setDiscountType("fixed");
            setDiscountPercentage(0);
            if (onUpdateDiscount) {
                onUpdateDiscount(0); // Clear percentage discount
            }
        } else {
            // Switch back to percentage mode when fixed amount is 0 or empty
            setDiscountType("percentage");
            // Restore preset discount if it exists, otherwise keep current percentage
            if (presetDiscount != null && onUpdateDiscount) {
                onUpdateDiscount(presetDiscount); // Restore preset discount
            }
        }
    };

    // Handle percentage discount button click
    const handlePercentageDiscountClick = (percentage) => {
        // Don't allow percentage changes if order has already been printed
        if (isOrderPrinted) {
            return;
        }
        if (isProviderDiscountBlocked) {
            setErrorMessage(t("workMode.providerDiscountNotAllowed"));
            return;
        }
        if (isStaffMeal) {
            return;
        }

        // Switching to percentage should clear any fixed discount so the total updates.
        if (fixedDiscountAmount > 0) {
            setFixedDiscountAmount(0);
        }
        const nextPercentage = toggleDiscountPercentage(
            discountPercentage,
            percentage
        );
        setDiscountType("percentage");
        setDiscountPercentage(nextPercentage);
        if (onUpdateDiscount) {
            onUpdateDiscount(nextPercentage);
        }
    };

    const loyaltyUsageText =
        loyalty?.maxDailyRedemptions > 0
            ? t("paymentWidget.loyaltyUsageToday", {
                  used: loyalty.timesRedeemedToday,
                  total: loyalty.maxDailyRedemptions,
              })
            : t("paymentWidget.loyaltyUsageTodayUnlimited", {
                  used: loyalty?.timesRedeemedToday || 0,
              });

    const loyaltyHelperText = isLoyaltyModeBlocked
        ? t("paymentWidget.loyaltyFullOrderOnly")
        : loyaltyError
          ? t("paymentWidget.loyaltyUnavailable")
          : loyalty?.disabledReason || "";

    const loyaltyCardNode = hasActiveLoyaltyProgram ? (
        <div className={classes.LoyaltyCard}>
            <div className="LoyaltyCard__Header">
                <div className="LoyaltyCard__HeaderLeft">
                    <div className="LoyaltyCard__IconBadge">
                        <LoyaltyOutlined fontSize="small" />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div className="LoyaltyCard__Title">
                            {t("paymentWidget.loyaltyTitle")}
                        </div>
                        <div className="LoyaltyCard__Subtitle">
                            {loyalty?.loyaltyProgramName ||
                                t("paymentWidget.loyaltyNoProgram")}
                        </div>
                    </div>
                </div>
                <FormControlLabel
                    className="LoyaltyCard__Toggle"
                    control={
                        <Switch
                            size="small"
                            color="primary"
                            checked={!!redeemLoyaltyPoints}
                            onChange={(_, checked) => {
                                setRedeemLoyaltyPoints(checked);
                                if (!checked) {
                                    setRedeemedPoints(0);
                                } else if (
                                    !redeemedPoints &&
                                    loyalty?.minimumRedemption > 0
                                ) {
                                    setRedeemedPoints(
                                        Math.min(
                                            loyalty.minimumRedemption,
                                            loyalty.loyaltyPoints
                                        )
                                    );
                                }
                                setErrorMessage(null);
                            }}
                            disabled={
                                isLoyaltyLoading ||
                                isLoyaltyModeBlocked ||
                                !loyalty?.hasProgram ||
                                !loyalty?.canRedeem
                            }
                        />
                    }
                    label={t("paymentWidget.loyaltyRedeemToggle")}
                    labelPlacement="start"
                />
            </div>

            <div className="LoyaltyCard__Stats">
                <div className="LoyaltyCard__StatItem">
                    <span className="LoyaltyCard__StatLabel">
                        {t("paymentWidget.loyaltyAvailablePoints")}
                    </span>
                    <span className="LoyaltyCard__StatValue">
                        {loyalty?.loyaltyPoints || 0}
                    </span>
                </div>
                <div className="LoyaltyCard__StatItem">
                    <span className="LoyaltyCard__StatLabel">
                        {t("paymentWidget.loyaltyConversionFactor")}
                    </span>
                    <span className="LoyaltyCard__StatValue">
                        ₪{Number(loyalty?.conversionFactor || 0).toFixed(2)}
                    </span>
                </div>
                <div className="LoyaltyCard__StatItem">
                    <span className="LoyaltyCard__StatLabel">
                        {t("paymentWidget.loyaltyValue")}
                    </span>
                    <span className="LoyaltyCard__StatValue">
                        ₪{Number(loyalty?.redemptionValue || 0).toFixed(2)}
                    </span>
                </div>
                <div className="LoyaltyCard__StatItem">
                    <span className="LoyaltyCard__StatLabel">
                        {t("paymentWidget.loyaltyRedeemedAmount")}
                    </span>
                    <span className="LoyaltyCard__StatValue LoyaltyCard__StatValue--accent">
                        ₪{appliedLoyaltyAmount.toFixed(2)}
                    </span>
                </div>
            </div>

            <div className="LoyaltyCard__InputRow">
                <TextField
                    className="LoyaltyCard__Input"
                    size="small"
                    label={t("paymentWidget.loyaltyPointsToRedeem")}
                    type="number"
                    value={redeemedPoints || ""}
                    onChange={(event) => {
                        const nextValue = Math.max(
                            0,
                            Math.floor(Number(event.target.value || 0))
                        );
                        setRedeemedPoints(nextValue);
                        setErrorMessage(null);
                    }}
                    disabled={
                        !redeemLoyaltyPoints ||
                        isLoyaltyLoading ||
                        isLoyaltyModeBlocked ||
                        !loyalty?.canRedeem
                    }
                    InputProps={{
                        style: {
                            fontSize: "15px",
                            fontWeight: 700,
                        },
                    }}
                    inputProps={{
                        min: 0,
                        step: 1,
                        max: loyalty?.loyaltyPoints || 0,
                    }}
                    fullWidth
                />
                <div className="LoyaltyCard__QuickActions">
                    <Button
                        className="LoyaltyCard__QuickBtn"
                        size="small"
                        variant="outlined"
                        disabled={
                            !redeemLoyaltyPoints ||
                            !loyalty?.minimumRedemption
                        }
                        onClick={() => {
                            setRedeemedPoints(
                                Math.min(
                                    loyalty.minimumRedemption,
                                    loyalty.loyaltyPoints || 0
                                )
                            );
                            setErrorMessage(null);
                        }}
                    >
                        {t("paymentWidget.loyaltyUseMinimum")}
                    </Button>
                    <Button
                        className="LoyaltyCard__QuickBtn"
                        size="small"
                        variant="outlined"
                        disabled={
                            !redeemLoyaltyPoints ||
                            !(loyalty?.maximumRedemption || loyalty?.loyaltyPoints)
                        }
                        onClick={() => {
                            const cap = loyalty?.maximumRedemption
                                ? Math.min(
                                      loyalty.maximumRedemption,
                                      loyalty.loyaltyPoints || 0
                                  )
                                : loyalty?.loyaltyPoints || 0;
                            setRedeemedPoints(cap);
                            setErrorMessage(null);
                        }}
                    >
                        {t("paymentWidget.loyaltyUseMaximum")}
                    </Button>
                </div>
            </div>

            {(loyaltyHelperText || loyaltyUsageText) && (
                <div className="LoyaltyCard__Helper">
                    {loyaltyHelperText || loyaltyUsageText}
                </div>
            )}

            {appliedLoyaltyAmount > 0 && (
                <div className="LoyaltyCard__AppliedBanner">
                    <span>
                        {t("paymentWidget.loyaltyPayableAmount", {
                            amount: payableOrderAmount.toFixed(2),
                        })}
                    </span>
                    <span>−₪{appliedLoyaltyAmount.toFixed(2)}</span>
                </div>
            )}
        </div>
    ) : null;

    return (
        <div
            data-terminal-workstation={terminalWorkstation}
            className={classes.AddPaymentWidgetWrap}
            style={{
                height: "95vh",
                maxHeight: "95vh",
                display: "flex",
                flexDirection: "column",
                position: "relative",
            }}
        >
            <div
                className={classes.AddPaymentWidget}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflow: "hidden",
                    position: "relative",
                }}
            >
                <RetailPromotionNotice offer={promotionOffer} />
                {/* Header - Fixed */}
                <div
                    className={classes.SmallTablePlaceholderWrapper}
                    style={{
                        flexShrink: 0,
                        zIndex: 1,
                    }}
                >
                    <SmallTablePlaceholder
                        backCallback={handleBackClicked}
                        tables={tables}
                        internalMessage={internalMessage}
                    />
                </div>

                {/* Scrollable Content Area */}
                <div
                    style={{
                        flex: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        paddingBottom: "80px", // Extra space for footer
                        minHeight: 0,
                    }}
                >
                    {/* Top Row: Order Amount, Change, and Currency Rate */}
                    <div
                        style={{
                            display: "flex",
                            gap: "8px",
                            marginBottom: "15px",
                        }}
                    >
                        {/* Order Amount Section */}
                        <div
                            style={{
                                backgroundColor: "#f0f8ff",
                                border: "1px solid #b6d7ff",
                                borderRadius: "6px",
                                padding: "10px 12px",
                                flex: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                cursor: "pointer",
                                minHeight: "50px",
                            }}
                            onClick={() =>
                                handleReplaceAmount(payableOrderAmount)
                            }
                        >
                            <span
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#1e3a8a",
                                }}
                            >
                                {isRetailPos
                                    ? t("retailPos.saleTotal")
                                    : t("paymentWidget.orderAmount")}
                            </span>
                            <span
                                style={{
                                    fontSize: "18px",
                                    fontWeight: "bold",
                                    color: "#1d4ed8",
                                }}
                            >
                                ₪{payableOrderAmount.toFixed(2)}
                            </span>
                        </div>

                        {/* Change Section */}
                        <div
                            style={{
                                backgroundColor: "#e8f5e8",
                                border: "1px solid #c3e6c3",
                                borderRadius: "6px",
                                padding: "10px 12px",
                                flex: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                minHeight: "50px",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#2d5a2d",
                                }}
                            >
                                {t("paymentWidget.change")}
                            </span>
                            <span
                                style={{
                                    fontSize: "18px",
                                    fontWeight: "bold",
                                    color: "#1e7e34",
                                }}
                            >
                                ₪{changeAmount === null ? "…" : changeAmount.toFixed(2)}
                            </span>
                        </div>

                        {/* Currency Rate Mini Card (only when all methods are in company currency) */}
                        {!hasForeignCurrencyPaymentMethod && (
                            <div
                                style={{
                                    backgroundColor: "#fff8e1",
                                    border: "1px solid #ffcc02",
                                    borderRadius: "6px",
                                    padding: "8px 10px",
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minHeight: "50px",
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: "12px",
                                        fontWeight: "500",
                                        color: "#e65100",
                                        marginBottom: "2px",
                                    }}
                                >
                                    {t("paymentWidget.usd")}
                                </span>
                                <span
                                    style={{
                                        fontSize: "14px",
                                        fontWeight: "bold",
                                        color: "#ef6c00",
                                    }}
                                >
                                    ₪{dollarRate?.toFixed(2) || "-.--"}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Loyalty Program Row (only when an active program exists) */}
                    {hasActiveLoyaltyProgram && (
                        <div style={{ marginBottom: "15px" }}>
                            {loyaltyCardNode}
                        </div>
                    )}

                    {/* Payment Summary (Dynamic) */}
                    <div
                        style={{
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #e9ecef",
                            borderRadius: "6px",
                            padding: "12px",
                            marginBottom: "15px",
                        }}
                    >
                        <div
                            style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                marginBottom: "10px",
                                color: "#333",
                                borderBottom: "1px solid #dee2e6",
                                paddingBottom: "6px",
                                textAlign: "center",
                            }}
                        >
                            {isRetailPos
                                ? t("retailPos.salePaymentSummary")
                                : t("paymentWidget.paymentSummary")}
                        </div>
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                justifyContent: "space-between",
                                alignItems: "stretch",
                                gap: "10px",
                            }}
                        >
                            {terminalError && <Typography color="error" role="alert">{["action", "connection"].includes(terminalError) ? t("terminalPayment.connectionError", { defaultValue: "Terminal checkout unavailable. Check connection or contact your manager before collecting payment." }) : terminalError}</Typography>}
                            {Object.values(terminalProviders).some(Boolean) && <Typography variant="caption" sx={{ width: "100%" }}>USB · {terminalDevices.map(device => `${device.terminal_name}: ${device.online ? t("terminalPayment.connected", { defaultValue: "Gateway connected" }) : t("terminalPayment.offline", { defaultValue: "Gateway unavailable" })}`).join(" / ") || t("terminalPayment.notAssigned", { defaultValue: "No terminal assigned" })} · {terminalWorkstation}</Typography>}
                            {paymentMethods.map((method) => {
                                const methodAmount = Number(paymentAmounts[method] || 0);
                                const isPaidMethod = methodAmount > 0;

                                return (
                                    <div
                                        key={method}
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            minWidth: "120px",
                                            flex: "1 1 120px",
                                            borderRadius: "8px",
                                            padding: "6px 8px",
                                            backgroundColor: isPaidMethod
                                                ? "#e9f7ef"
                                                : "transparent",
                                            border: isPaidMethod
                                                ? "1px solid #b7e4c7"
                                                : "1px solid transparent",
                                        }}
                                    >
                                        <span
                                            style={{
                                                color: isPaidMethod ? "#1b5e20" : "#666",
                                                fontSize: "12px",
                                                marginBottom: "3px",
                                                textAlign: "center",
                                                fontWeight: isPaidMethod ? "600" : "400",
                                            }}
                                        >
                                            {method}{terminalProviders[method] ? ` · ${terminalProviders[method] === "bop_x990" ? "BOP USB" : terminalProviders[method]}` : ""}
                                        </span>
                                        <span
                                            style={{
                                                fontWeight: "bold",
                                                color: isPaidMethod ? "#1b5e20" : "#333",
                                                fontSize: "16px",
                                            }}
                                        >
                                            {getCurrencySymbol(
                                                methodCurrencyMap[method] || companyCurrency
                                            )}
                                            {methodAmount.toFixed(2)}
                                        </span>
                                        {(methodCurrencyMap[method] || companyCurrency).toUpperCase() !==
                                            companyCurrency.toUpperCase() ? (
                                            <span
                                                style={{
                                                    color: isPaidMethod
                                                        ? "#2e7d32"
                                                        : "#6E7187",
                                                    fontSize: "12px",
                                                    marginTop: "2px",
                                                }}
                                            >
                                                ≈ {getCurrencySymbol(companyCurrency)}
                                                {Number(
                                                    paymentAmountsInCompanyCurrency[method] || 0
                                                ).toFixed(2)}
                                            </span>
                                        ) : null}
                                    </div>
                                );
                            })}
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    minWidth: "120px",
                                    flex: "1 1 120px",
                                }}
                            >
                                <span
                                    style={{
                                        color: "#666",
                                        fontSize: "12px",
                                        marginBottom: "3px",
                                    }}
                                >
                                    {t("paymentWidget.total")}
                                </span>
                                <span
                                    style={{
                                        fontWeight: "bold",
                                        color: "#2c3e50",
                                        fontSize: "16px",
                                    }}
                                >
                                    ₪{totalPaidInCompanyCurrency === null ? "…" : totalPaidInCompanyCurrency.toFixed(2)}
                                </span>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    minWidth: "120px",
                                    flex: "1 1 120px",
                                }}
                            >
                                <span
                                    style={{
                                        color: "#666",
                                        fontSize: "12px",
                                        marginBottom: "3px",
                                    }}
                                >
                                    {t("paymentWidget.remaining")}
                                </span>
                                <span
                                    style={{
                                        fontWeight: "bold",
                                        color:
                                            remainingAmount > 0
                                                ? "#e74c3c"
                                                : "#28a745",
                                        fontSize: "16px",
                                    }}
                                >
                                    ₪{remainingAmount === null ? "…" : remainingAmount.toFixed(2)}
                                </span>
                            </div>
                        </div >
                        {isProviderPayment && (
                            <Typography role="status" sx={{ mt: 1, overflowWrap: "anywhere" }}>
                                {t("Provider balance")}: {Math.max(0, remainingAmount).toFixed(2)} {companyCurrency}
                                {" — "}{order?.delivery_provider || t("Select a delivery provider")}
                            </Typography>
                        )}
                    </div >

                    <div className={classes.MainContainer}>
                        <div>
                            {/* Quick Amount Buttons */}
                            <div className={classes.Tips}>
                                <div className="Tips__Amounts">
                                    {[10, 20, 50, 100, 200].map((entry) => {
                                        return (
                                            <div
                                                key={entry}
                                                onClick={() =>
                                                    handleReplaceAmount(entry)
                                                }
                                                className={classNames(
                                                    "Number",
                                                    {
                                                        IsSelected:
                                                            selectedTip ===
                                                            entry,
                                                    }
                                                )}
                                            >
                                                {entry}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Calculator Keypad */}
                            <div className={classes.Keys}>
                                {[9, 8, 7, 6, 5, 4, 3, 2, 1, 0].map((entry) => {
                                    return (
                                        <div
                                            key={entry}
                                            className={classNames("Number")}
                                            onClick={() =>
                                                handleUpdateAmount(entry)
                                            }
                                        >
                                            {entry}
                                        </div>
                                    );
                                })}
                                <div className="Clear" onClick={handleClearAll}>
                                    {t("paymentWidget.clearShort")}
                                </div>
                                <div
                                    className="Delete"
                                    onClick={handleUpdateClear}
                                >
                                    <img src={clear} alt={t("common.delete")} />
                                </div>
                            </div>
                        </div>

                        <div className={classes.PaymentType}>
                            <div className={classes.VerticalContainer}>
                                {errorMessage && (
                                    <div
                                        style={{
                                            color: "red",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        {errorMessage}
                                    </div>
                                )}

                                {/* Payment Method Selection */}
                                <div style={{ marginBottom: "15px" }}>
                                    <div
                                        style={{
                                            fontSize: "14px",
                                            marginBottom: "8px",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {t("paymentWidget.activePaymentMethod")}
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            width: "100%",
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        {paymentMethods.map((method) => (
                                            <Button
                                                key={method}
                                                onClick={() =>
                                                    handlePaymentTypeSwitch(method)
                                                }
                                                style={getPaymentTypeButtonStyle(
                                                    method
                                                )}
                                            >
                                                {method} (
                                                {getCurrencySymbol(
                                                    methodCurrencyMap[method] || companyCurrency
                                                )}
                                                {Number(
                                                    paymentAmounts[method] || 0
                                                ).toFixed(2)}
                                                )
                                            </Button>
                                        ))}
                                    </div >
                                </div >

                                {/* Current Input Display */}
                                < div style={{ marginBottom: "15px" }}>
                                    <TextField
                                        label={`${activePaymentMethod || (isRetailPos ? t("retailPos.payment") : "Payment")} ${
                                            isRetailPos
                                                ? t("retailPos.amount")
                                                : t("paymentWidget.amount")
                                        }`}
                                        value={currentInput}
                                        variant="outlined"
                                        // fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    {getCurrencySymbol(
                                                        methodCurrencyMap[activePaymentMethod] ||
                                                        companyCurrency
                                                    )}
                                                </InputAdornment>
                                            ),
                                            readOnly: true,
                                            style: {
                                                fontSize: "18px",
                                                fontWeight: "bold",
                                            },
                                        }}
                                        style={{ backgroundColor: "#f5f5f5" }}
                                    />
                                </div >

                                {/* Discount Section */}
                                < div >
                                    {isRetailPos &&
                                    retailOnDemandDiscountEnabled && !promotionOffer ? (
                                        <Button
                                            variant={
                                                retailDiscountOpen
                                                    ? "contained"
                                                    : "outlined"
                                            }
                                            startIcon={<LocalOfferOutlined />}
                                            onClick={() =>
                                                setRetailDiscountOpen(
                                                    (isOpen) => !isOpen
                                                )
                                            }
                                            sx={{ mb: retailDiscountOpen ? 1.5 : 0 }}
                                        >
                                            {retailDiscountOpen
                                                ? t("retailPos.hideDiscount")
                                                : t("retailPos.addDiscount")}
                                        </Button>
                                    ) : null}
                                    {canShowDiscountControls && (
                                        <div className={classes.Discount}>
                                            {/* Fixed Amount Discount Input */}
                                            {discountAmountAllowed && (
                                                <div
                                                    style={{
                                                        marginBottom: "15px",
                                                    }}
                                                >
                                                    <TextField
                                                        label={t(
                                                            "paymentWidget.fixedDiscountAmount"
                                                        )}
                                                        type="number"
                                                        value={
                                                            fixedDiscountAmount ||
                                                            ""
                                                        }
                                                        onChange={
                                                            handleFixedDiscountChange
                                                        }
                                                        variant="outlined"
                                                        // size="small"
                                                        disabled={
                                                            isOrderPrinted ||
                                                            isProviderDiscountBlocked ||
                                                            isStaffMeal
                                                        }
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    ₪
                                                                </InputAdornment>
                                                            ),
                                                            style: {
                                                                fontSize:
                                                                    "18px",
                                                                fontWeight:
                                                                    "bold",
                                                            },
                                                        }}
                                                        inputProps={{
                                                            min: 0,
                                                            step: 0.01,
                                                        }}
                                                        style={{
                                                            backgroundColor:
                                                                presetDiscountAmount !=
                                                                    null &&
                                                                    presetDiscountAmount >
                                                                    0 &&
                                                                    payingAll
                                                                    ? "#f0f0f0"
                                                                    : "#f5f5f5",
                                                            // width: "100%",
                                                            // backgroundColor: "white"
                                                        }}
                                                        helperText={
                                                            isProviderDiscountBlocked
                                                                ? t(
                                                                    "workMode.providerDiscountNotAllowed"
                                                                )
                                                                :
                                                                presetDiscountAmount !=
                                                                    null &&
                                                                    presetDiscountAmount >
                                                                    0 &&
                                                                    payingAll
                                                                    ? t(
                                                                        "paymentWidget.presetDiscountAmount"
                                                                    )
                                                                    : !payingAll
                                                                        ? t(
                                                                            "paymentWidget.discountSelectedItems"
                                                                        )
                                                                        : discountType ===
                                                                            "fixed" && isOrderPrinted
                                                                            ? t(
                                                                                "paymentWidget.fixedDiscountApplied"
                                                                            )
                                                                            : t(
                                                                                "paymentWidget.enterFixedDiscount"
                                                                            )
                                                        }
                                                    />
                                                </div>
                                            )}

                                            {/* Percentage Discount Buttons */}
                                            <div
                                                className={`Discount__Amounts ${isOrderPrinted || isStaffMeal
                                                    ? "Disabled"
                                                    : ""
                                                    }`}
                                            >
                                                {discountPercentageOptions.map(
                                                    (entry) => {
                                                        return (
                                                            <div
                                                                key={entry}
                                                                onClick={() =>
                                                                    isOrderPrinted || isProviderDiscountBlocked || isStaffMeal
                                                                        ? console.log(
                                                                            "Discount mode disabled"
                                                                        )
                                                                        : handlePercentageDiscountClick(
                                                                            entry
                                                                        )
                                                                }
                                                                className={classNames(
                                                                    "Number",
                                                                    "DiscountButton",
                                                                    {
                                                                        IsSelected:
                                                                            discountPercentage ===
                                                                            entry,
                                                                    }
                                                                )}
                                                                style={{
                                                                    opacity:
                                                                        isOrderPrinted ||
                                                                            isProviderDiscountBlocked ||
                                                                            isStaffMeal
                                                                            ? 0.5
                                                                            :
                                                                            1,
                                                                    cursor:
                                                                        isOrderPrinted ||
                                                                            isProviderDiscountBlocked ||
                                                                            isStaffMeal
                                                                            ? "not-allowed"
                                                                            : "pointer",
                                                                }}
                                                            >
                                                                {entry}%
                                                            </div>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div >

                                {/* Special Payment Options */}
                                < div
                                    style={{ width: "100%", marginTop: "15px" }}
                                >
                                    <ToggleButtonGroup
                                        style={{
                                            width: "100%",
                                            display: "flex",
                                        }}
                                        value={selectedButton}
                                        exclusive
                                        onChange={handleButtonChange}
                                        aria-label={t("paymentWidget.paymentOptionsAria")}
                                    >
                                        {!isRetailPos ? (
                                            <ToggleButton
                                                value="OTH"
                                                aria-label={t(
                                                    "paymentWidget.orderOnHouse"
                                                )}
                                                style={getButtonStyle1("OTH")}
                                            >
                                                {t("paymentWidget.orderOnHouse")}
                                            </ToggleButton>
                                        ) : null}
                                        {allowStaffMeal ? (
                                            <ToggleButton
                                                value="StaffMeal"
                                                aria-label={t(
                                                    "paymentWidget.staffMeal"
                                                )}
                                                style={getButtonStyle1("StaffMeal")}
                                            >
                                                {t("paymentWidget.staffMeal")}
                                            </ToggleButton>
                                        ) : null}
                                        {providerPaymentEnabled && payingAll && (
                                            <ToggleButton value="Provider" aria-label="Provider"
                                                style={getButtonStyle1("Provider")}>
                                                {t("Provider")}
                                            </ToggleButton>
                                        )}
                                        <ToggleButton
                                            value="Deferred"
                                            aria-label={t("paymentWidget.deferred")}
                                            style={getButtonStyle1("Deferred")}
                                        >
                                            {t("paymentWidget.deferred")}
                                        </ToggleButton>
                                        {selectedButton &&
                                            selectedButton === "Deferred" && (
                                                <div>
                                                    <IconButton
                                                        className={
                                                            classes.Table_edit_customer
                                                        }
                                                        onClick={() =>
                                                            changeCustomer(
                                                                selectedCustomer
                                                            )
                                                        }
                                                    >
                                                        <PersonAddAlt1
                                                            fontSize="x-small"
                                                            color="white"
                                                        />
                                                    </IconButton>
                                                </div>
                                            )}
                                    </ToggleButtonGroup>
                                </div >

                                {selectedButton && (
                                    <div
                                        style={{
                                            marginTop: "10px",
                                            marginBottom: "20px",
                                        }}
                                    >
                                        {!isRetailPos && selectedButton === "OTH" && (
                                            <div>
                                                {t(
                                                    "paymentWidget.orderOnHouseSelected"
                                                )}
                                            </div>
                                        )}
                                        {selectedButton === "StaffMeal" && (
                                            <div>
                                                <div style={{ marginBottom: 10 }}>
                                                    {t(
                                                        "paymentWidget.staffMealSelectEmployee"
                                                    )}
                                                </div>
                                                <Autocomplete
                                                    options={staffMealEmployees || []}
                                                    getOptionLabel={(option) =>
                                                        option.employee_name ||
                                                        option.name ||
                                                        ""
                                                    }
                                                    value={
                                                        (staffMealEmployees || []).find(
                                                            (emp) =>
                                                                emp.name ===
                                                                staffMealEmployee
                                                        ) || null
                                                    }
                                                    onChange={(_, newValue) => {
                                                        const employeeId = newValue?.name || "";
                                                        setStaffMealEmployee(employeeId);
                                                        setErrorMessage(null);
                                                        if (onStaffMealEmployeeChange) {
                                                            onStaffMealEmployeeChange(employeeId);
                                                        }
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={t(
                                                                "paymentWidget.staffMeal"
                                                            )}
                                                            size="small"
                                                            fullWidth
                                                        />
                                                    )}
                                                    renderOption={(props, option) => (
                                                        <Box
                                                            component="li"
                                                            {...props}
                                                        >
                                                            <Typography variant="body2">
                                                                {option.employee_name ||
                                                                    option.name}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                />
                                                {staffMealEmployee && invoice ? (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ mt: 1, color: "text.secondary" }}
                                                    >
                                                        {t(
                                                            "paymentWidget.staffMealPayrollDeduction",
                                                            "Payroll deduction: {{amount}} {{currency}}",
                                                            {
                                                                amount: Number(
                                                                    invoice.grand_total ??
                                                                        invoice.rounded_total ??
                                                                        0
                                                                ).toFixed(2),
                                                                currency: companyCurrency,
                                                            }
                                                        )}
                                                    </Typography>
                                                ) : null}
                                            </div>
                                        )}
                                        {selectedButton === "Deferred" && (
                                            <div>
                                                {selectedCustomer ? (
                                                    <div>
                                                        {t(
                                                            "paymentWidget.paymentFor",
                                                            { name: selectedCustomer }
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div>
                                                        {t(
                                                            "paymentWidget.selectCustomer"
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div >
                        </div >
                    </div >
                </div >

                {/* Fixed Footer - Always Visible at Bottom */}

                < button
                    className="Btn--Primary"
                    onClick={() => {
                        if (confirmClickLockedRef.current) return;
                        confirmClickLockedRef.current = true;
                        const ok = validateInput();
                        if (!ok) {
                            // Unlock immediately if validation failed
                            confirmClickLockedRef.current = false;
                        }
                    }}
                    disabled={
                        hasBlockingMutation ||
                        isCreatingInvoice ||
                        isLoading ||
                        totalPaidInCompanyCurrency === null ||
                        !!posProfileCompanyMismatch
                    }
                >
                    {hasBlockingMutation || isCreatingInvoice || isLoading ? (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CircularProgress
                                size={20}
                                style={{ marginRight: "10px", color: "white" }}
                            />
                            {t("paymentWidget.confirmingPayment")}
                        </div>
                    ) : (
                        isRetailPos
                            ? t("retailPos.confirmPaymentPrintReceipt")
                            : t("paymentWidget.confirmPaymentPrintReceipt")
                    )}
                </button >

                <CustomerDialog
                    dialogOpen={changeCustomerDialogOpen}
                    onCancel={handleCustomerChangeClose}
                    onConfirm={onConfirmCustomerChanged}
                    selectedCustomer={selectedCustomer}
                    customerSelected={onCustomerSelected}
                ></CustomerDialog>
            </div >
        </div >
    );
};

export default AddPaymentWidget;
